import { AlertTriangle, ArrowLeft, ArrowRight, ChevronLeft, Download, RefreshCw, Wallet } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { WalletMovementsFilters } from "../components/WalletMovementsFilters";
import { WalletMovementsTable } from "../components/WalletMovementsTable";
import { WalletService } from "../services/walletsService";
import type { WalletMovementsFilterKind } from "../types/wallet-movements-filters.types";
import type {
  OrganizationWalletBalance,
  WalletTransactionEntity,
  WalletTransactionFilters,
} from "../types/wallets-api.types";
import { formatUSD, isIncomingDirection, sumByDirection } from "../utils/walletMovementFormat";

const PAGE_SIZE = 10;

export const WalletMovementsPage = () => {
  const { t } = useTranslations("cards");
  const { t: tGlobal } = useTranslations("global");
  const navigate = useNavigate();
  const { user } = useAuth();

  const organizationId = user?.organization_id;

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_gestion") },
      { label: tGlobal("sidebar_menu_cards"), to: "/app/cards/cards" },
      { label: t("wallet_movements_breadcrumb") },
    ],
    [t, tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const [walletId, setWalletId] = useState<string | null>(null);
  const [balance, setBalance] = useState<OrganizationWalletBalance | null>(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(true);

  const [filter, setFilter] = useState<WalletMovementsFilterKind>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [movements, setMovements] = useState<WalletTransactionEntity[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setIsLoadingBalance(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoadingBalance(true);
      try {
        const res = await WalletService.getOrganizationCardWalletBalance(organizationId);
        if (cancelled) return;
        setWalletId(res.data.wallet.id);
        setBalance(res.data.balance);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(HttpApiService.getErrorMessage(err) || t("wallet_movements_error_load"));
      } finally {
        if (!cancelled) setIsLoadingBalance(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, t]);

  const fetchMovements = useCallback(async () => {
    if (!walletId) return;
    setIsLoading(true);
    setError(null);
    try {
      const apiFilters: WalletTransactionFilters = {
        skip: (currentPage - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      if (filter !== "all") apiFilters.transaction_type = filter;

      const response = await WalletService.getWalletTransactions(walletId, apiFilters);
      const data = response.data;
      setMovements(Array.isArray(data.data) ? data.data : []);
      setTotalItems(typeof data.total === "number" ? data.total : 0);
    } catch (err: unknown) {
      setError(HttpApiService.getErrorMessage(err) || t("wallet_movements_error_load"));
      setMovements([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [walletId, filter, currentPage, t]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const handleFilterChange = useCallback((next: WalletMovementsFilterKind) => {
    setFilter(next);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      const lastPage = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
      const safePage = page > lastPage ? 1 : Math.max(1, page);
      setCurrentPage(safePage);
    },
    [totalItems],
  );

  const handleExport = useCallback(() => {
    colocaToast.info(t("wallet_banner_topup_coming_soon"));
  }, [t]);

  const handleRefresh = useCallback(() => {
    fetchMovements();
    if (organizationId) {
      WalletService.getOrganizationCardWalletBalance(organizationId)
        .then((res) => setBalance(res.data.balance))
        .catch(() => {
          // balance stays as-is if the refresh fails; the list refresh is the primary action
        });
    }
  }, [fetchMovements, organizationId]);

  const handleTopup = useCallback(() => {
    colocaToast.info(t("wallet_banner_topup_coming_soon"));
  }, [t]);

  const inTotal = useMemo(() => sumByDirection(movements, "IN"), [movements]);
  const outTotal = useMemo(() => sumByDirection(movements, "OUT"), [movements]);
  const inCount = useMemo(
    () => movements.filter((m) => isIncomingDirection(m.direction)).length,
    [movements],
  );
  const outCount = useMemo(
    () => movements.filter((m) => !isIncomingDirection(m.direction)).length,
    [movements],
  );

  const currentBalance = balance ? Number(balance.current.balance) : 0;
  const isInitialLoad = isLoadingBalance || (isLoading && movements.length === 0);
  const isEmpty = !isInitialLoad && !isLoading && !error && movements.length === 0;

  return (
    <div className="space-y-6 p-2">
      <button
        type="button"
        onClick={() => navigate("/app/cards/cards")}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        {t("wallet_movements_eyebrow_back")}
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold leading-tight tracking-tight text-foreground">
            {t("wallet_movements_title")}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("wallet_movements_subtitle")}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ColocaButton variant="default" icon={RefreshCw} onClick={handleRefresh} loading={isLoading}>
            {t("wallet_movements_refresh")}
          </ColocaButton>
          <ColocaButton variant="default" icon={Download} onClick={handleExport}>
            {t("wallet_movements_export")}
          </ColocaButton>
        </div>
      </div>

      {error && (
        <ColocaAlert
          variant="destructive"
          icon={AlertTriangle}
          heading={t("wallet_movements_error_load")}
          text={error}
        />
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          label={t("wallet_movements_stat_balance_label")}
          description={t("wallet_movements_stat_balance_description")}
          value={formatUSD(currentBalance)}
          isLoading={isLoadingBalance}
        />
        <StatCard
          label={t("wallet_movements_stat_in_label")}
          description={`${inCount} ${t("wallet_movements_direction_in").toLowerCase()}`}
          value={`+${formatUSD(inTotal)}`}
          accent="positive"
          isLoading={isLoading && movements.length === 0}
        />
        <StatCard
          label={t("wallet_movements_stat_out_label")}
          description={`${outCount} ${t("wallet_movements_direction_out").toLowerCase()}`}
          value={`−${formatUSD(outTotal)}`}
          accent="negative"
          isLoading={isLoading && movements.length === 0}
        />
      </div>

      <section className="overflow-hidden rounded-2xl border border-border bg-background shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold leading-tight text-foreground">
              {t("wallet_movements_history_title")}
            </h2>
            <ColocaBadge variant="secondary">{totalItems}</ColocaBadge>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <WalletMovementsFilters value={filter} onChange={handleFilterChange} />
            <ColocaButton variant="default" size="sm" iconTrailing={ArrowRight} onClick={handleTopup}>
              {t("wallet_movements_history_transfer_button")}
            </ColocaButton>
          </div>
        </div>

        {isInitialLoad ? (
          <div className="divide-y divide-border">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3.5">
                <ColocaSkeleton className="h-8 w-8 shrink-0 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <ColocaSkeleton className="h-3.5 w-48" />
                  <ColocaSkeleton className="h-2.5 w-32" />
                </div>
                <ColocaSkeleton className="hidden h-4 w-24 sm:block" />
                <ColocaSkeleton className="h-5 w-16 rounded-full" />
                <ColocaSkeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          <div className="p-6">
            <ColocaEmpty
              icon={Wallet}
              title={t("wallet_movements_empty")}
              description={t("wallet_movements_empty_description")}
            />
          </div>
        ) : (
          <div className="relative">
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm">
                <div className="flex flex-col items-center gap-3">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  <span className="text-sm text-muted-foreground">{t("wallet_movements_loading")}</span>
                </div>
              </div>
            )}
            <WalletMovementsTable movements={movements} />
            <ColocaPagination
              page={currentPage}
              perPage={PAGE_SIZE}
              total={totalItems}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </section>

      <div className="flex justify-start">
        <ColocaButton variant="ghost" icon={ArrowLeft} onClick={() => navigate("/app/cards/cards")}>
          {t("wallet_movements_back")}
        </ColocaButton>
      </div>
    </div>
  );
};

type StatCardProps = {
  label: string;
  description: string;
  value: string;
  accent?: "positive" | "negative";
  isLoading?: boolean;
};

const StatCard = ({ label, description, value, accent, isLoading }: StatCardProps) => (
  <div className="rounded-2xl border border-border bg-background p-5 shadow-sm">
    <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
    {isLoading ? (
      <ColocaSkeleton className="mt-1.5 h-8 w-40" />
    ) : (
      <p
        className={
          accent === "positive"
            ? "mt-1.5 text-3xl font-semibold font-mono tabular-nums tracking-tight text-emerald-600 dark:text-emerald-400"
            : accent === "negative"
              ? "mt-1.5 text-3xl font-semibold font-mono tabular-nums tracking-tight text-red-600 dark:text-red-400"
              : "mt-1.5 text-3xl font-semibold font-mono tabular-nums tracking-tight text-foreground"
        }
      >
        {value}
      </p>
    )}
    <p className="mt-1 text-xs text-muted-foreground">{description}</p>
  </div>
);
